import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import useQueries from '@/hooks/useQueries';
import { API_URL } from '@/shared/utils/API_URL';
import { buildQuery } from '@/shared/utils/buildQuery';
import useLogsController from './useLogsController';
import useEntities from './createOrder/useEntities';

export interface ExpenseAccount {
    account_id: string;
    account_name: string;
    accounttype?: string;
}

export interface Project {
    type_id: string;
    type_name: string;
}

export interface ExpenseVoucherFormData {
    date: string;
    projectId: string;
    amount: string;
}

const CASH_ACCOUNT_ID = '201';

const toEnglishDigits = (str: any): string => {
    if (str === null || str === undefined) return '';
    return String(str)
        .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 1632))
        .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 1776));
};


const insertMoneyRequest = async ({
    insertMoneyQuery,
    licenceId,
    sessionId,
    selectedAccount,
    amount,
    moneyString,
    queries,
}: any) => {
    const moneyResponse = await axios.post(
        API_URL,
        { q: insertMoneyQuery },
        { params: { t: 'in', id: licenceId, s: sessionId, btn: 'w_itm', api: 'itqan' } }
    );

    const moneyId = moneyResponse?.data?.data?.result;

    const valuesString = [
        `(${moneyId}, '${selectedAccount!.account_id}', 'النقدي', 0, ${amount}, 'Cash', 0, 0)`,
        `(${moneyId}, '${CASH_ACCOUNT_ID}', 'النقدي', ${amount}, 0, 'Cash', 0, 0)`
    ].join(', ');

    const insertMoneyDetQuery = buildQuery(queries?.insertMoneyDetQuery, {
        valuesString,
    });

    const detResponse = await axios.post(
        API_URL,
        { q: insertMoneyDetQuery },
        { params: { t: 'in', id: licenceId, s: sessionId, btn: 'w_itm', api: 'itqan' } }
    );

    return { ...detResponse.data, money_id: moneyId };
};
const formatWithCurrentTime = (dateStr: string) => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${dateStr} ${hours}:${minutes}:${seconds}`;
};

const useCreateExpenseVoucherController = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { log } = useLogsController();

    const queries = useQueries();
    const sessionId = Cookies.get('sessionId');
    const licenceId = localStorage.getItem('licence_id');
    const { projectsWithBusiness } = useEntities();


    const [searchTerm, setSearchTerm] = useState('');
    const [accountResults, setAccountResults] = useState<ExpenseAccount[]>([]);
    const [isSearchingAccounts, setIsSearchingAccounts] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<ExpenseAccount | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [formData, setFormData] = useState<ExpenseVoucherFormData>({
        date: new Date().toISOString().slice(0, 10),
        projectId: '',
        amount: ''
    });

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);


    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (!searchTerm.trim() || !queries) {
            setAccountResults([]);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            try {
                setIsSearchingAccounts(true);
                const searchQuery = buildQuery(queries.searchExpenseAccountsQuery, {
                    search: searchTerm.trim()
                });
                const res = await axios.post(
                    API_URL,
                    { q: searchQuery },
                    { params: { t: 'sel', id: licenceId, s: sessionId, btn: 'w_itm', api: 'itqan' } }
                );
                setAccountResults(res?.data?.data?.result ?? []);
            } finally {
                setIsSearchingAccounts(false);
            }
        }, 350);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [searchTerm, queries]);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setSelectedAccount(null);
    };

    const selectAccount = (account: ExpenseAccount) => {
        setSelectedAccount(account);
        setSearchTerm(`${account.account_name}`);
        setAccountResults([]);
    };

    const handleFormInputChange = (field: keyof ExpenseVoucherFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const isValid = () =>
        !!selectedAccount &&
        !!formData.date &&
        !!formData.projectId &&
        Number(formData.amount) > 0;


    const mutation = useMutation({
        mutationFn: async () => {

            const fullDateTime = formatWithCurrentTime(formData.date);

            const amount = Number(formData.amount);
            const moneyString = `سند صرف بتاريخ ${fullDateTime?.slice(0, 10)}`;
            const moneyUserId = 1;

            const insertMoneyQuery = buildQuery(queries?.insertMoneyQuery, {
                money_string: moneyString,
                money_date: toEnglishDigits(fullDateTime),
                project_id: toEnglishDigits(formData.projectId),
                money_user_id: 1
            });

            return insertMoneyRequest({
                insertMoneyQuery,
                licenceId,
                sessionId,
                selectedAccount,
                amount,
                moneyString,
                queries,
            });
        },
        onSuccess: (data: any) => {
            const moneyId = data?.money_id;
            log({
                action: 'CREATE',
                opname: 'createExpenseVoucher',
                title: t('expenseVoucherCreated', {
                    moneyId,
                    accountName: selectedAccount?.account_name
                }),
                refid: moneyId,
                ltype: 'MONEY',
                ldata: {
                    accountId: selectedAccount?.account_id,
                    accountName: selectedAccount?.account_name,
                    projectId: toEnglishDigits(formData.projectId),
                    amount: toEnglishDigits(formData.amount),
                    date: formData.date,
                },
                lnote: '',
            });

            toast.success(t('Expense voucher created successfully!'));
            setShowSuccessModal(true);
        },
        onError: (error: any) => {
            toast.error(error?.message || t('Error creating expense voucher'));
        },
    });


    const createExpenseVoucher = async () => {
        setIsSubmitted(true);
        if (!isValid()) {
            toast.error(t('Please fill in all required fields'));
            return;
        }

        void mutation.mutate();
    };

    const resetForm = () => {
        setSelectedAccount(null);
        setSearchTerm('');
        setAccountResults([]);
        setFormData({
            date: new Date().toISOString().slice(0, 10),
            projectId: '',
            amount: ''
        });
        setIsSubmitted(false);
        setShowSuccessModal(false);
    };
    
    return {
        t,
        projects: projectsWithBusiness,
        searchTerm,
        accountResults,
        isSearchingAccounts,
        selectedAccount,
        formData,
        isSubmitted,
        isUpdating: mutation.isPending,
        showSuccessModal,
        handleSearchChange,
        selectAccount,
        handleFormInputChange,
        createExpenseVoucher,
        resetForm,
    }
}

export default useCreateExpenseVoucherController;